import { MinimalUserDto, SaveAttachmentDto } from '@issuemanager/api-client';
import { ControlledFileUploader } from '@issuemanager/apps/client/components';
import { useAttachmentsContext } from '@issuemanager/apps/client/contexts';
import {
  useAllMutations,
  useMonitorFinishActionSubmit,
} from '@issuemanager/apps/client/hooks';
import { useFinishAction } from '@issuemanager/client/features/issues';
import { IssuePermissionsEnum } from '@issuemanager/constants';
import { useEventCallback } from '@mui/material';
import { useSnackbar } from 'notistack';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useIssueOutletContext } from '../../../hooks/useIssueOutletContext';
import { LayoutPanel } from '../layout';

type Props = {
  onClose: () => void;
  isOpen: boolean;
  actionId: string;
  actionTitle: string;
};

type FormValues = {
  attachments: (SaveAttachmentDto & {
    addedBy: MinimalUserDto;
    createdAt: string;
  })[];
};

export function ActionFinishPanel({
  onClose,
  isOpen,
  actionId,
  actionTitle,
}: Props) {
  const { t } = useTranslation('action');
  const { enqueueSnackbar } = useSnackbar();
  const { code: issueCode } = useIssueOutletContext();
  const { isLoading: isUploadAttachmentsLoading } = useAttachmentsContext();

  const finishAction = useFinishAction({
    issueCode,
    onSuccess: () =>
      enqueueSnackbar(t('manage.finish.success'), {
        variant: 'success',
      }),
  });

  const monitorFinishActionSubmit = useMonitorFinishActionSubmit();

  const form = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      attachments: [],
    },
  });

  useAllMutations({
    mutations: [finishAction],
    onStart: () => {
      monitorFinishActionSubmit.start();
    },
    onSuccess: () => {
      monitorFinishActionSubmit.stop();
    },
    onError: () => {
      monitorFinishActionSubmit.error();
    },
  });

  const handleSubmit = useEventCallback(() => {
    form.handleSubmit(onSubmit)();
  });

  const onSubmit = async ({ attachments }: FormValues) => {
    await finishAction.mutateAsync({ id: actionId, attachments });
    onClose();
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  return (
    <LayoutPanel
      isOpen={isOpen}
      onCancel={handleCancel}
      title={t('manage.finish.title')}
      description={t('manage.finish.description', {
        action: actionTitle,
      })}
      actionButton={{
        disabled: finishAction.isPending || isUploadAttachmentsLoading,
        'data-testid': 'finish-footer-button',
      }}
      cancelButton={{
        disabled: isUploadAttachmentsLoading,
      }}
      actionButtonText={
        finishAction.isPending
          ? t('manage.finish.accepting')
          : t('manage.finish.accept')
      }
      isLoading={finishAction.isPending}
      onSend={handleSubmit}
      content={
        <FormProvider {...form}>
          <form data-testid='form-finish-action'>
            <ControlledFileUploader
              control={form.control}
              name='attachments'
              disabled={finishAction.isPending}
              permissions={[
                IssuePermissionsEnum.IsActionApprover,
                IssuePermissionsEnum.IsActionOwner,
              ]}
            />
          </form>
        </FormProvider>
      }
    />
  );
}
